/**
 * Funções de estilo do OpenLayers para as camadas vetoriais.
 *
 * Cada fábrica recebe o estilo efetivo (`LayerStyle`) e devolve uma função de
 * estilo com cache próprio; quando o usuário muda o estilo, o gerenciador de
 * camadas cria uma nova função (o cache antigo é descartado junto).
 */
import Style, { type StyleFunction } from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import Fill from 'ol/style/Fill';
import Text from 'ol/style/Text';
import CircleStyle from 'ol/style/Circle';
import type { FeatureLike } from 'ol/Feature';
import type Geometry from 'ol/geom/Geometry';
import Point from 'ol/geom/Point';
import Polygon from 'ol/geom/Polygon';
import MultiPolygon from 'ol/geom/MultiPolygon';
import LineString from 'ol/geom/LineString';
import MultiLineString from 'ol/geom/MultiLineString';
import { haloFor, hexToRgba } from './styleConfig';
import { createGeometryMemo, createStyleCache } from './styleCache';
import { isSedeLabelVisible, maxRiverOrderForLabels, polygonBigEnough } from './labelDensity';
import type { LayerStyle } from './types';
import { cascoDeLinha, sobreImagem } from '../map/contraste';

export const LABEL_FONT_FAMILY = 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';

const CASING = 'rgba(6,10,18,0.55)';

function font(weight: number, size: number, italic = false): string {
  return `${italic ? 'italic ' : ''}${weight} ${Math.round(size * 10) / 10}px ${LABEL_FONT_FAMILY}`;
}

/** Espessura do halo proporcional ao tamanho do texto. */
export function haloWidth(size: number): number {
  return Math.max(2.5, Math.round(size * 0.24 * 10) / 10);
}

/** Preenchimento invisível: faz o clique dentro do polígono funcionar sem pintar nada. */
const HIT_FILL = new Fill({ color: 'rgba(0,0,0,0)' });

function num(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) ? n : fallback;
}

// ---------------------------------------------------------------------------
// Geometrias auxiliares para rótulos (memoizadas por feature)
// ---------------------------------------------------------------------------

interface PolyInfo {
  /** Ponto interior do maior polígono (um só rótulo por feature, mesmo em MultiPolygon). */
  point: Point | undefined;
  /** Área do maior polígono (unidades do mapa²). */
  area: number;
}

export const polygonInfo = createGeometryMemo<PolyInfo>((f) => {
  const g = f.getGeometry();
  if (g instanceof Polygon) {
    return { point: new Point(g.getInteriorPoint().getCoordinates().slice(0, 2)), area: g.getArea() };
  }
  if (g instanceof MultiPolygon) {
    let best: Polygon | undefined;
    let bestArea = -1;
    for (const p of g.getPolygons()) {
      const a = p.getArea();
      if (a > bestArea) {
        bestArea = a;
        best = p;
      }
    }
    return {
      point: best ? new Point(best.getInteriorPoint().getCoordinates().slice(0, 2)) : undefined,
      area: Math.max(0, bestArea),
    };
  }
  return { point: undefined, area: 0 };
});

interface LineInfo {
  /** Ponto do meio da parte mais longa. */
  point: Point | undefined;
  /** Comprimento da parte mais longa (unidades do mapa). */
  length: number;
}

export const lineInfo = createGeometryMemo<LineInfo>((f) => {
  const g = f.getGeometry();
  let best: LineString | undefined;
  if (g instanceof LineString) best = g;
  else if (g instanceof MultiLineString) {
    let bestLen = -1;
    for (const l of g.getLineStrings()) {
      const len = l.getLength();
      if (len > bestLen) {
        bestLen = len;
        best = l;
      }
    }
  }
  if (!best) return { point: undefined, length: 0 };
  return { point: new Point(best.getCoordinateAt(0.5)), length: best.getLength() };
});

const wrapCache = createStyleCache<string>(2048);

/** Quebra rótulos longos em linhas de até ~`maxChars` caracteres. */
export function wrapLabel(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text;
  return wrapCache.get(`${maxChars}|${text}`, () => {
    const words = text.split(/\s+/);
    const lines: string[] = [];
    let line = '';
    for (const w of words) {
      if (line && line.length + 1 + w.length > maxChars) {
        lines.push(line);
        line = w;
      } else {
        line = line ? `${line} ${w}` : w;
      }
    }
    if (line) lines.push(line);
    return lines.join('\n');
  });
}

// ---------------------------------------------------------------------------
// Polígonos simples (municípios, TIs)
// ---------------------------------------------------------------------------

/** Só o preenchimento (camada em Z.VECTOR_FILL). */
export function polygonFillStyle(s: LayerStyle): Style {
  return new Style({ fill: new Fill({ color: hexToRgba(s.fillColor, s.fillOpacity) }) });
}

/**
 * Linha fina sem casco: sobre a foto de satélite do fundo (map/contraste.ts) ganha o
 * casco escuro por baixo — o contorno de 0,6 px dos municípios sumia nas ruas.
 * Sobre o GOES fica como sempre foi.
 */
function comCasco(line: Style, width: number, resolution: number): Style | Style[] {
  return sobreImagem(resolution) ? [cascoDeLinha(width), line] : line;
}

/** Contorno + preenchimento invisível para o clique (camada em Z.VECTOR_LINE). */
export function polygonOutlineStyle(s: LayerStyle): StyleFunction {
  const line = new Style({
    stroke: new Stroke({ color: s.strokeColor, width: s.strokeWidth, lineJoin: 'round' }),
    fill: HIT_FILL,
  });
  return (_feature, resolution) => comCasco(line, s.strokeWidth, resolution);
}

/** Linha com "casco" escuro por baixo (legível sobre nuvens brancas). */
export function casedLineStyles(color: string, width: number): Style[] {
  return [
    new Style({ stroke: new Stroke({ color: CASING, width: width + 2, lineJoin: 'round', lineCap: 'round' }), zIndex: 0 }),
    new Style({ stroke: new Stroke({ color, width, lineJoin: 'round', lineCap: 'round' }), zIndex: 1 }),
  ];
}

/** Divisas de sub-regiões: linha com casco (legível sobre nuvens) + preenchimento invisível para o clique. */
export function regionOutlineStyles(s: LayerStyle): Style[] {
  const [casing, line] = casedLineStyles(s.strokeColor, s.strokeWidth);
  line.setFill(HIT_FILL);
  return [casing, line];
}

// ---------------------------------------------------------------------------
// Unidades de conservação: cor por grupo
// ---------------------------------------------------------------------------

/** true = Proteção Integral; false = Uso Sustentável (ou desconhecido). */
export function isProtecaoIntegral(grupo: unknown): boolean {
  return typeof grupo === 'string' && /integral/i.test(grupo);
}

export function ucColor(s: LayerStyle, grupo: unknown): string {
  return isProtecaoIntegral(grupo) ? s.strokeColor : s.altColor;
}

export function ucFillStyleFunction(s: LayerStyle): StyleFunction {
  const cache = createStyleCache<Style>(8);
  return (feature) => {
    const pi = isProtecaoIntegral(feature.get('grupo'));
    return cache.get(pi ? 'pi' : 'us', () => new Style({ fill: new Fill({ color: hexToRgba(ucColor(s, feature.get('grupo')), s.fillOpacity) }) }));
  };
}

export function ucOutlineStyleFunction(s: LayerStyle): StyleFunction {
  const cache = createStyleCache<Style>(8);
  return (feature, resolution) => {
    const pi = isProtecaoIntegral(feature.get('grupo'));
    const line = cache.get(
      pi ? 'pi' : 'us',
      () =>
        new Style({
          stroke: new Stroke({ color: ucColor(s, feature.get('grupo')), width: s.strokeWidth, lineJoin: 'round' }),
          fill: HIT_FILL,
        }),
    );
    return comCasco(line, s.strokeWidth, resolution);
  };
}

// ---------------------------------------------------------------------------
// Rios: espessura por ordem
// ---------------------------------------------------------------------------

/** Espessura do rio conforme a ordem (1 = principal … 3 = menor). */
export function riverWidth(base: number, ordem: number): number {
  const factor = ordem <= 1 ? 1 : ordem === 2 ? 0.6 : 0.4;
  return Math.max(0.4, Math.round(base * factor * 10) / 10);
}

export function riverOrder(feature: FeatureLike): number {
  return Math.min(3, Math.max(1, Math.round(num(feature.get('ordem'), 3))));
}

export function riosLineStyleFunction(s: LayerStyle): StyleFunction {
  const cache = createStyleCache<Style>(8);
  return (feature, resolution) => {
    const ordem = riverOrder(feature);
    const line = cache.get(
      String(ordem),
      () =>
        new Style({
          stroke: new Stroke({ color: s.strokeColor, width: riverWidth(s.strokeWidth, ordem), lineJoin: 'round', lineCap: 'round' }),
        }),
    );
    return comCasco(line, riverWidth(s.strokeWidth, ordem), resolution);
  };
}

const labelLines = new WeakMap<FeatureLike, { bucket: number; geometry: Geometry | undefined }>();

/**
 * Linha simplificada (tolerância ≈ 6 px na resolução atual) só para posicionar
 * o nome: rios reais são muito sinuosos e o OL não encontra trecho "reto" o
 * bastante (maxAngle) na geometria original. Memoizada por meio nível de zoom.
 */
export function labelLine(feature: FeatureLike, resolution: number): Geometry | undefined {
  const bucket = Math.round(Math.log2(resolution) * 2);
  const hit = labelLines.get(feature);
  if (hit && hit.bucket === bucket) return hit.geometry;
  const g = feature.getGeometry();
  const geometry =
    g instanceof LineString || g instanceof MultiLineString ? g.simplify(2 ** (bucket / 2) * 6) : undefined;
  labelLines.set(feature, { bucket, geometry });
  return geometry;
}

export function riosLabelStyleFunction(s: LayerStyle): StyleFunction {
  const cache = createStyleCache<Style>(8);
  return (feature, resolution) => {
    const nome = feature.get('nome');
    if (typeof nome !== 'string' || !nome) return undefined;
    const ordem = riverOrder(feature);
    if (ordem > maxRiverOrderForLabels(resolution)) return undefined;
    const line = labelLine(feature, resolution);
    if (!line) return undefined;
    const style = cache.get(String(ordem), () => {
      const size = Math.max(8, s.labelSize + (ordem === 1 ? 1 : ordem === 3 ? -1 : 0));
      return new Style({
        text: new Text({
          placement: 'line',
          // Sem `repeat`: o OL procura o trecho mais reto da linha para o nome
          // (com `repeat`, posições fixas em trechos sinuosos são descartadas).
          maxAngle: Math.PI / 4,
          font: font(ordem === 1 ? 600 : 500, size, true),
          fill: new Fill({ color: s.labelColor }),
          stroke: new Stroke({ color: haloFor(s.labelColor), width: haloWidth(size) }),
          padding: [2, 4, 2, 4],
        }),
      });
    });
    style.setGeometry(line);
    style.getText()!.setText(nome);
    return style;
  };
}

// ---------------------------------------------------------------------------
// Rodovias
// ---------------------------------------------------------------------------

/** Rodovia federal (BR-xxx)? */
export function isFederalRoad(ref: unknown): boolean {
  return typeof ref === 'string' && /^\s*BR\b/i.test(ref);
}

/** "BR-163;BR-230" → "BR-163 / BR-230". */
export function formatRoadRef(ref: unknown): string {
  if (typeof ref !== 'string') return '';
  const parts = [...new Set(ref.split(/[;,/]/).map((p) => p.trim()).filter(Boolean))];
  return parts.join(' / ');
}

export function rodoviasLineStyleFunction(s: LayerStyle): StyleFunction {
  const cache = createStyleCache<Style[]>(8);
  return (feature) => {
    const federal = isFederalRoad(feature.get('ref'));
    return cache.get(federal ? 'br' : 'uf', () =>
      casedLineStyles(s.strokeColor, federal ? s.strokeWidth : Math.max(0.4, Math.round(s.strokeWidth * 0.7 * 10) / 10)),
    );
  };
}

export function rodoviasLabelStyleFunction(s: LayerStyle): StyleFunction {
  const style = new Style({
    geometry: (f) => lineInfo(f).point,
    text: new Text({
      font: font(700, s.labelSize),
      fill: new Fill({ color: s.labelColor }),
      backgroundFill: new Fill({ color: s.altColor }),
      backgroundStroke: new Stroke({ color: 'rgba(255,255,255,0.85)', width: 1 }),
      padding: [2, 4, 1, 4],
    }),
  });
  return (feature, resolution) => {
    const ref = formatRoadRef(feature.get('ref'));
    if (!ref) return undefined;
    // Trechos curtos na tela não ganham placa (evita poluição no zoom afastado).
    if (lineInfo(feature).length / resolution < 90) return undefined;
    style.getText()!.setText(ref);
    return style;
  };
}

// ---------------------------------------------------------------------------
// Sedes municipais (nomes das cidades)
// ---------------------------------------------------------------------------

type SedeClass = 'capital' | 'big' | 'mid' | 'small';

export function sedeClass(nivel: unknown): SedeClass {
  const n = num(nivel, 4);
  return n <= 1 ? 'capital' : n === 2 ? 'big' : n === 3 ? 'mid' : 'small';
}

const SEDE_LOOK: Record<SedeClass, { radius: number; sizeDelta: number; weight: number }> = {
  capital: { radius: 4.2, sizeDelta: 2, weight: 700 },
  big: { radius: 3.2, sizeDelta: 0, weight: 600 },
  mid: { radius: 2.6, sizeDelta: -1, weight: 500 },
  small: { radius: 2.2, sizeDelta: -1.5, weight: 500 },
};

function buildSedeStyle(s: LayerStyle, cls: SedeClass): Style {
  const look = SEDE_LOOK[cls];
  const size = Math.max(8, s.labelSize + look.sizeDelta);
  const image = s.points
    ? new CircleStyle({
        radius: look.radius,
        fill: new Fill({ color: s.strokeColor }),
        stroke: new Stroke({ color: 'rgba(6,10,18,0.9)', width: cls === 'capital' ? 1.6 : 1.1 }),
      })
    : undefined;
  const text = s.labels
    ? new Text({
        font: font(look.weight, size),
        fill: new Fill({ color: s.labelColor }),
        stroke: new Stroke({ color: haloFor(s.labelColor), width: haloWidth(size) }),
        textBaseline: s.points ? 'bottom' : 'middle',
        offsetY: s.points ? -(look.radius + 1.5) : 0,
        padding: [2, 3, 2, 3],
        overflow: true,
      })
    : undefined;
  return new Style({ image, text });
}

/**
 * Nomes das cidades: densidade por resolução e `nivel`; a hierarquia visual
 * (tamanho/peso da fonte, raio do ponto) acompanha o nível.
 */
export function sedesStyleFunction(s: LayerStyle): StyleFunction {
  const cache = createStyleCache<Style>(8);
  return (feature, resolution) => {
    if (!s.labels && !s.points) return undefined;
    const nivel = feature.get('nivel');
    if (!isSedeLabelVisible(nivel, resolution, s.density)) return undefined;
    const cls = sedeClass(nivel);
    const style = cache.get(cls, () => buildSedeStyle(s, cls));
    if (s.labels) style.getText()!.setText(String(feature.get('nome') ?? ''));
    return style;
  };
}

/** Prioridade de rótulo das sedes: capitais primeiro, depois mais populosas. */
export function sedesRenderOrder(a: FeatureLike, b: FeatureLike): number {
  return num(a.get('nivel'), 4) - num(b.get('nivel'), 4) || num(b.get('pop'), 0) - num(a.get('pop'), 0);
}

// ---------------------------------------------------------------------------
// Rótulos de polígonos (siglas dos estados, nomes de TIs e UCs)
// ---------------------------------------------------------------------------

export interface PolygonLabelOptions {
  /** Propriedade com o texto. */
  prop: string;
  weight?: number;
  sizeDelta?: number;
  italic?: boolean;
  /** Quebra de linha a partir de N caracteres (0 = não quebra). */
  wrap?: number;
  /** Tamanho mínimo do polígono na tela (px) para ganhar rótulo (0 = sempre). */
  minPx?: number;
}

export function polygonLabelStyleFunction(s: LayerStyle, opts: PolygonLabelOptions): StyleFunction {
  const { prop, weight = 600, sizeDelta = 0, italic = false, wrap = 0, minPx = 0 } = opts;
  const size = Math.max(8, s.labelSize + sizeDelta);
  // Um único Style: a geometria (ponto do rótulo) e o texto variam por feature.
  const style = new Style({
    geometry: (f) => polygonInfo(f).point,
    text: new Text({
      font: font(weight, size, italic),
      fill: new Fill({ color: s.labelColor }),
      stroke: new Stroke({ color: haloFor(s.labelColor), width: haloWidth(size) }),
      padding: [2, 3, 2, 3],
      overflow: true,
    }),
  });
  return (feature, resolution) => {
    const raw = feature.get(prop);
    if (typeof raw !== 'string' || !raw) return undefined;
    if (minPx > 0 && !polygonBigEnough(polygonInfo(feature).area, resolution, minPx)) return undefined;
    style.getText()!.setText(wrap > 0 ? wrapLabel(raw, wrap) : raw);
    return style;
  };
}

/** Prioridade de rótulo por área (maiores primeiro). */
export function areaRenderOrder(a: FeatureLike, b: FeatureLike): number {
  return polygonInfo(b).area - polygonInfo(a).area;
}

// ---------------------------------------------------------------------------
// Destaque da área clicada
// ---------------------------------------------------------------------------

export function highlightStyles(): Style[] {
  return [
    new Style({ stroke: new Stroke({ color: 'rgba(6,10,18,0.7)', width: 5, lineJoin: 'round' }), zIndex: 0 }),
    new Style({
      stroke: new Stroke({ color: '#fde047', width: 2.5, lineJoin: 'round' }),
      fill: new Fill({ color: 'rgba(253,224,71,0.14)' }),
      zIndex: 1,
    }),
  ];
}
